import { useState, useEffect } from 'react';
import httpClient from '../lib/httpClient';
import { ENDPOINTS } from '../services/api/endpoints';
import { handleApiError } from '../services/toast';

// HaloPSA countries without any regions on file get this sentinel from the read side so a dropdown
// always has something selectable. It must never be written back as a real region value — see
// isSelectableRegion below.
export const NO_REGION_OPTION = { id: 0, value: '0', label: 'No region available', name: 'No region available' };

const formatOptions = (items) =>
  (items || []).map((item) => ({
    id: item.id,
    value: item.id.toString(),
    label: item.name,
    name: item.name,
  }));

/**
 * A HaloPSA region (lookup 77: `{ id, name, value2 }`) as a dropdown option. `code` is Halo's short
 * code (`value2`, e.g. "CA") — what an address's Address Line 4 stores; the dropdown still shows the name.
 */
export const toRegionOption = (item) => ({
  id: item.id,
  value: item.id.toString(),
  label: item.name,
  name: item.name,
  code: item.value2 || null,
});

/**
 * The value an address's Address Line 4 stores for a picked region: its short code, or its name when
 * HaloPSA has no code for it. Accepts a formatted option or a raw `{ name, value2 }` region.
 */
export function regionLine4Value(region) {
  return region?.code || region?.value2 || region?.name || region?.label || '';
}

const normaliseRegionText = (text) => String(text ?? '').trim().toLowerCase();

/**
 * The option a saved Address Line 4 refers to: matched by short code first, then by name — addresses
 * saved before Line 4 held codes still carry the full name until they are next saved.
 */
export function findRegionOption(options, saved) {
  const wanted = normaliseRegionText(saved);
  if (!wanted || !options?.length) return null;
  return (
    options.find((option) => option.code && normaliseRegionText(option.code) === wanted) ??
    options.find((option) => normaliseRegionText(option.label ?? option.name) === wanted) ??
    null
  );
}

/**
 * Loads the country list once, and the region list for whichever `countryId` is currently
 * selected. Designed to be called from ONE place that owns the country selection — the billing
 * and shipping address blocks share a single HaloPSA Site country/region pair (see
 * docs discussion: "billing/shipping-address" plan), so both address blocks read `regions` from
 * this same hook instance rather than each loading their own copy.
 */
export function useCountryRegionOptions(countryId) {
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingCountries(true);
        const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_COUNTRIES);
        const list = response.data?.data?.countries || [];
        if (!cancelled) setCountries(formatOptions(list));
      } catch (error) {
        // NOT silent: Country is a required field, so an empty list leaves the address form
        // unfillable and unsavable. Without a message the user just sees an empty dropdown and a
        // save that never succeeds. Same message as useBillingAddress/EditBillingDetailsDialog.
        if (!cancelled) {
          setCountries([]);
          handleApiError(error, 'Failed to load countries.');
        }
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!countryId) {
      setRegions([]);
      return undefined;
    }

    const load = async () => {
      try {
        setLoadingRegions(true);
        const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_REGIONS_BY_COUNTRY(countryId));
        const formatted = (response.data?.data?.regions || []).map(toRegionOption);
        if (!cancelled) setRegions(formatted.length > 0 ? formatted : [NO_REGION_OPTION]);
      } catch {
        // Deliberately silent: Region/State is optional on every address form, and the backend
        // returns 400 for a country that simply has no regions on file. Degrading to an empty,
        // disabled dropdown is the intended outcome — a toast here would fire on that ordinary
        // case and read as a failure to the user. Country above is the one that must speak up.
        if (!cancelled) setRegions([]);
      } finally {
        if (!cancelled) setLoadingRegions(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [countryId]);

  return { countries, loadingCountries, regions, loadingRegions };
}

/**
 * True for a real region option — false for the "No region available" sentinel that
 * useCountryRegionOptions injects when a country has none. Callers must filter with this before
 * writing a region value to HaloPSA: for billing it would send regionId 0, and for shipping's
 * free-text `county` field it would write the literal string "No region available".
 */
export function isSelectableRegion(region) {
  return !!region && region.id !== 0;
}

// The country every address form starts on when nothing is saved yet. Matched by name rather than id —
// HaloPSA's country ids are tenant data — tolerating the usual spellings of the same country.
const DEFAULT_COUNTRY_NAMES = new Set(['united states', 'united states of america', 'usa', 'us']);

/**
 * The default country option (United States) from a loaded country list, or null when the list does
 * not contain it. Only for a form with NO saved country — a saved country always wins.
 */
export function findDefaultCountry(countries) {
  return (
    (countries || []).find((c) =>
      DEFAULT_COUNTRY_NAMES.has(String(c?.name ?? c?.label ?? '').trim().toLowerCase())
    ) ?? null
  );
}
